<template>
    <div v-if="showSection">
        <div class="form" v-if="showEmailSection">
            <EmailInput
                @onStartEditing="onStartEditing"
                @onEndEditing="onEndEditing"
                v-model:requirement-type="emailRequirement"
                v-model:value="emailAddressValue"
                ref="emailComponent"
            />
        </div>

        <div v-if="ordererRequirements!.isPhoneNumberRequirementEnabled">
            <PhoneNumberInput
                @onStartEditing="onStartEditing"
                @onEndEditing="onEndEditing"
                v-model:requirement-type="phoneNumberRequirement"
                ref="phoneNumberComponent"
                v-if="phoneNumberRequirement !== InputRequirementType.NotRequired"
            />
        </div>

        <div class="form" v-if="ordererRequirements!.isFirstNameRequirementEnabled">
            <TextInput
                @onStartEditing="onStartEditing"
                @onEndEditing="onEndEditing"
                v-if="firstNameRequirement !== InputRequirementType.NotRequired"
                class="first-name-input"
                ref="firstNameComponent"
                :maxCharLength="60"
                v-model:value="firstName"
                :requirement-type="firstNameRequirement"
                :label="translations.general.firstName"
            />
        </div>

        <div class="form" v-if="ordererRequirements!.isLastNameRequirementEnabled">
            <TextInput
                @onStartEditing="onStartEditing"
                @onEndEditing="onEndEditing"
                v-if="lastNameRequirement !== InputRequirementType.NotRequired"
                class="last-name-input"
                ref="lastNameComponent"
                :maxCharLength="60"
                :requirement-type="lastNameRequirement"
                v-model:value="lastName"
                :label="translations.general.lastName"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { InputRequirementType } from "@/components/elements/InputRequirementType";
    import type { IContactDataValidationResult } from "@/components/pay/ContactDataValidationResult";
    import type { IEnteredContactData } from "@/components/pay/EnteredContactData";
    import { Factory } from "@/data/dao/Factory";
    import { CustomerDao } from "@/data/dao/service/CustomerDao";
    import {
        OrdererEmailRequirement,
        OrdererNameRequirement,
        OrdererPhoneNumberRequirement,
        OrdererRegisteredRequirement,
    } from "@/data/model/Enums";
    import { useCartStore } from "@/data/stores/cart";
    import { useIdentityStore } from "@/data/stores/identity";
    import { translations } from "@/shared/localization/translations";
    import { logInformation } from "@/shared/logging/informations";
    import { computed, ref } from "vue";

    import EmailInput from "@/components/elements/EmailInput.vue";
    import PhoneNumberInput from "@/components/elements/PhoneNumberInput.vue";
    import TextInput from "@/components/elements/TextInput.vue";
    import { Popup } from "@/components/dialog/Popup";

    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
    const cart = useCartStore();
    const identity = useIdentityStore();

    const props = defineProps<{
        onStartEditing?: () => void;
        onEndEditing?: () => void;
    }>()

    const ordererRequirements = computed(() => {
        return cart.orderService?.useCase?.ordererRequirements;
    });

    const hasOperatorAccess = computed(() => {
        return identity.hasOperatorAccess;
    });

    const showEmailSection = computed<boolean>(() => {
        if (hasOperatorAccess.value) {
            if (isRegisteredRequired.value) {
                return true;
            } else {
                return ordererRequirements.value!.isEmailRequirementEnabled;
            }
        } else {
            return (
                ordererRequirements.value?.emailRequirement === OrdererEmailRequirement.Required ||
                ordererRequirements.value?.emailRequirement === OrdererEmailRequirement.Optional
            );
        }
    });

    const showSection = computed<boolean>(() => {
        const value =
            ordererRequirements.value?.enabledFieldRequirementsCount === 1 ||
            (hasOperatorAccess.value &&
                ordererRequirements.value?.enabledFieldRequirementsCount === 0 &&
                isRegisteredRequired.value);

        logInformation("Show section: " + value);
        return value;
    });

    const firstName = ref<string | undefined>(cart.currentCart?.contactDataEndUser?.firstName);
    const lastName = ref<string | undefined>(cart.currentCart?.contactDataEndUser?.lastName);
    const emailAddressValue = ref<string | undefined>(cart.currentCart?.contactDataEndUser?.email);

    // ref
    const firstNameRequirement = ref<InputRequirementType>(
        convertToInputRequirementType(ordererRequirements.value?.firstNameRequirement)
    );
    const lastNameRequirement = ref<InputRequirementType>(
        convertToInputRequirementType(ordererRequirements.value?.lastNameRequirement)
    );
    const emailRequirement = ref<InputRequirementType>(
        convertEmailToInputRequirementType(
            ordererRequirements.value?.emailRequirement,
            ordererRequirements.value?.registeredRequirement
        )
    );
    const phoneNumberRequirement = ref<InputRequirementType>(
        convertPhoneToInputRequirementType(ordererRequirements.value?.phoneNumberRequirement)
    );

    const phoneNumberComponent = ref<InstanceType<typeof PhoneNumberInput> | undefined>();
    const firstNameComponent = ref<InstanceType<typeof TextInput> | undefined>();
    const lastNameComponent = ref<InstanceType<typeof TextInput> | undefined>();
    const emailComponent = ref<InstanceType<typeof EmailInput> | undefined>();

    const isRegisteredRequired = computed<boolean>(
        () => ordererRequirements.value?.isRegisteredRequirementEnabled === true
    );

    function getCurrentContactData(): IEnteredContactData {
        const phoneNumberResult = phoneNumberComponent.value?.getResult();
        const currentFirstName = firstName.value;
        const currentLastName = lastName.value;
        const currentEmailAddress = emailAddressValue.value;

        return {
            firstName: currentFirstName,
            lastName: currentLastName,
            email: currentEmailAddress,
            phoneNumber: phoneNumberResult?.isValid ? phoneNumberResult?.phoneNumber : undefined,
            phoneNumberCountryCode: phoneNumberResult?.isValid ? phoneNumberResult?.countryCode : undefined,
        };
    }

    async function validateAndCreateOrUpdateContactData(
        showMissingInfoPopup: boolean = false
    ): Promise<IContactDataValidationResult> {
        phoneNumberComponent.value?.setError(false);
        firstNameComponent.value?.setError(false);
        lastNameComponent.value?.setError(false);
        emailComponent.value?.setError(false);

        const phoneNumberResult = phoneNumberComponent.value?.getResult();
        const currentFirstName = firstName.value;
        const currentLastName = lastName.value;
        const currentEmailAddress = emailAddressValue.value;

        let isDataValid = true;

        if (firstNameComponent.value != null && !firstNameComponent.value!.isValid()) {
            isDataValid = false;
            firstNameComponent.value!.setError(true);

            if (showMissingInfoPopup) {
                Popup.show({
                    message: identity.hasOperatorAccess ? translations.pay.pleaseEnterRequiredEndUserData : translations.pay.pleaseEnterRequiredData,
                    uniqueId: "please-enter-required-data-popup",
                });
            }
        }

        if (lastNameComponent.value != null && !lastNameComponent.value!.isValid()) {
            isDataValid = false;
            lastNameComponent.value!.setError(true);

            if (showMissingInfoPopup) {
                Popup.show({
                    message: identity.hasOperatorAccess ? translations.pay.pleaseEnterRequiredEndUserData : translations.pay.pleaseEnterRequiredData,
                    uniqueId: "please-enter-required-data-popup",
                });
            }
        }

        if (phoneNumberComponent.value != null) {
            if (!phoneNumberResult!.isValid) {
                isDataValid = false;
                phoneNumberComponent.value!.setError(true);

                if (showMissingInfoPopup) {
                    Popup.show({
                        message: identity.hasOperatorAccess ? translations.pay.pleaseEnterRequiredEndUserData : translations.pay.pleaseEnterRequiredData,
                        uniqueId: "please-enter-required-data-popup",
                    });
                }
            }
        }

        if (emailComponent.value != null && !emailComponent.value!.isValid()) {
            isDataValid = false;
            emailComponent.value!.setError(true);

            if (showMissingInfoPopup) {
              if (identity.hasOperatorAccess) {
                Popup.show({
                  message: translations.pay.pleaseEnterEndUsersEmail,
                  uniqueId: "please-enter-email-popup",
                });
              } else {
                Popup.show({
                  message: translations.pay.pleaseEnterYourEmail,
                  uniqueId: "please-enter-required-data-popup",
                });
              }
            }
        }

        if (isDataValid) {
            let contactDataEndUser = await customerDao.updateOrCreateOrderCustomerContactData({
                firstName: currentFirstName,
                lastName: currentLastName,
                email: emailComponent.value == undefined ? undefined : currentEmailAddress,
                phoneNumber: phoneNumberComponent.value == undefined ? undefined : phoneNumberResult?.phoneNumber,
                phoneCountryCode: phoneNumberComponent.value == undefined ? undefined : phoneNumberResult?.dialCode,
                indirectIdentityId: cart.currentCart?.contactDataEndUser?.identityId,
            });

            cart.setContactData(
                contactDataEndUser.firstName,
                contactDataEndUser.lastName,
                contactDataEndUser.email,
                contactDataEndUser.phoneNumber,
                contactDataEndUser.identityId
            );

            if (!identity.hasOperatorAccess) {
                identity.onIdentityChanged({ isLogout: false, profile: contactDataEndUser });
            }

            return {
                contactData: {
                    identityId: contactDataEndUser.identityId!,
                    firstName: contactDataEndUser.firstName,
                    lastName: contactDataEndUser.lastName,
                    email: contactDataEndUser.email,
                    phoneNumber: contactDataEndUser.phoneNumber,
                },
                isValid: true,
            };
        }
        return {
            contactData: undefined,
            isValid: false,
        };
    }

    function reset() {
        firstName.value = cart.currentCart?.contactDataEndUser?.firstName;
        lastName.value = cart.currentCart?.contactDataEndUser?.lastName;
        emailAddressValue.value = cart.currentCart?.contactDataEndUser?.email;
        phoneNumberComponent.value?.setValue(cart.currentCart?.contactDataEndUser?.phoneNumber);
    }

    function convertToInputRequirementType(
        ordererNameRequirement: OrdererNameRequirement | undefined
    ): InputRequirementType {
        switch (ordererNameRequirement) {
            case undefined:
            case OrdererNameRequirement.NotRequired:
                return InputRequirementType.NotRequired;
            case OrdererNameRequirement.Optional:
                return InputRequirementType.Optional;
            case OrdererNameRequirement.Required:
                return InputRequirementType.Required;
            case null:
                throw new Error("No orderer requirement found");
        }

        throw new Error("Unknown orderer requirement");
    }

    function convertEmailToInputRequirementType(
        ordererEmailRequirement: OrdererEmailRequirement | undefined,
        orderRegisteredRequirement: OrdererRegisteredRequirement | undefined
    ): InputRequirementType {
        if (orderRegisteredRequirement === OrdererRegisteredRequirement.Required && hasOperatorAccess.value) {
            logInformation("Show email");
            return InputRequirementType.Required;
        }

        switch (ordererEmailRequirement) {
            case OrdererEmailRequirement.NotRequired:
                return InputRequirementType.NotRequired;
            case OrdererEmailRequirement.Optional:
                return InputRequirementType.Optional;
            case OrdererEmailRequirement.Required:
                return InputRequirementType.Required;
            case OrdererEmailRequirement.ConfirmedBlockOrder:
                if (hasOperatorAccess.value) {
                    logInformation("Show email");
                    return InputRequirementType.Required; // Show the field to provide registered end user email
                } else {
                    logInformation("not Show email");
                    return InputRequirementType.NotRequired;
                }
            case null:
                throw new Error("No orderer requirement found");
        }

        throw new Error("Unknown orderer requirement");
    }
    function convertPhoneToInputRequirementType(
        ordererNameRequirement: OrdererPhoneNumberRequirement | undefined
    ): InputRequirementType {
        switch (ordererNameRequirement) {
            case undefined:
            case OrdererPhoneNumberRequirement.NotRequired:
                return InputRequirementType.NotRequired;
            case OrdererPhoneNumberRequirement.Optional:
                return InputRequirementType.Optional;
            case OrdererPhoneNumberRequirement.Required:
            case OrdererPhoneNumberRequirement.ConfirmedBlockOrder:
                return InputRequirementType.Required;
        }

        throw new Error("Unknown orderer requirement");
    }

    function onStartEditing() {
        if (props.onStartEditing != null) {
            props.onStartEditing();
        }
    }

    function onEndEditing() {
        if (props.onEndEditing != null) {
            props.onEndEditing();
        }
    }

    defineExpose({
        validateAndCreateOrUpdateContactData,
        reset,
        getCurrentContactData,
    });
</script>

<style scoped lang="scss"></style>
